import { useState } from 'react';

export default function SignatureMoments({ audit, onUpdate }) {
  const [generating, setGenerating] = useState(false);
  const moments = audit.signatureMoments || [];

  const lockedCount = moments.filter(m => m.locked).length;
  const unlockedCount = moments.length - lockedCount;

  const toggleLock = (index) => {
    const updated = [...moments];
    updated[index] = { ...updated[index], locked: !updated[index].locked };
    onUpdate({ ...audit, signatureMoments: updated });
  };

  const generateMoments = async () => {
    setGenerating(true);
    try {
      const apiKey = import.meta.env.VITE_ANTHROPIC_API_KEY;
      const intake = audit.intake || {};
      const scores = audit.scores || {};
      const ai = audit.aiResearch || {};

      const locked = moments.filter(m => m.locked);
      const slotsNeeded = Math.max(5 - locked.length, 1);

      const lockedContext = locked.length > 0
        ? `\n\nThe client has already locked these moments (do NOT repeat or duplicate them, generate DIFFERENT ones):\n${locked.map(m => `- ${m.title}: ${m.description}`).join('\n')}`
        : '';

      const prompt = `You are a hospitality experience designer for Next Experience Group. Based on this business audit, suggest ${slotsNeeded} specific "only here" signature moments this restaurant/bar should create.

Concept: ${intake.conceptName || 'Unknown'}
City: ${intake.city || 'Unknown'}
Concept Clarity Score: ${scores.concept_clarity?.score || 'N/A'}
Brand Expression Score: ${scores.brand_expression?.score || 'N/A'}
Emotional Experience Score: ${scores.emotional_experience?.score || 'N/A'}
Beverage Score: ${scores.beverage_intelligence?.score || 'N/A'}
Guest Signal themes: ${JSON.stringify(ai.guestSentiment?.combined?.positiveThemes || [])}
Competitor weaknesses: ${(Array.isArray(audit.approvedCompetitors) ? audit.approvedCompetitors : []).map(c => c?.weaknesses).filter(Boolean).join('; ')}
Executive Diagnostic: ${ai.executiveDiagnostic || ''}${lockedContext}

Each moment should be specific, actionable, tied to their concept, and memorable.

Return ONLY this JSON array:
[{"title":"","description":"","why":"","revenueImpact":"","difficulty":"Easy|Medium|Hard"}]`;

      const response = await fetch('https://api.anthropic.com/v1/messages', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'x-api-key': apiKey, 'anthropic-version': '2023-06-01', 'anthropic-dangerous-direct-browser-access': 'true' },
        body: JSON.stringify({ model: 'claude-sonnet-4-6', max_tokens: 2000, messages: [{ role: 'user', content: prompt }] }),
      });

      const data = await response.json();
      const text = data.content?.[0]?.text || '';
      const match = text.match(/\[[\s\S]*\]/);
      if (match) {
        const parsed = JSON.parse(match[0]);
        // Merge: keep locked items in place, replace unlocked with new
        const merged = [];
        let newIndex = 0;
        for (const m of moments) {
          if (m.locked) {
            merged.push(m);
          } else if (newIndex < parsed.length) {
            merged.push(parsed[newIndex++]);
          }
        }
        // Append any remaining new items
        while (newIndex < parsed.length) {
          merged.push(parsed[newIndex++]);
        }
        onUpdate({ ...audit, signatureMoments: merged });
      }
    } catch (err) {
      console.error('Signature moment generation failed:', err);
    }
    setGenerating(false);
  };

  const updateMoment = (index, field, value) => {
    const updated = [...moments];
    updated[index] = { ...updated[index], [field]: value };
    onUpdate({ ...audit, signatureMoments: updated });
  };

  const deleteMoment = (index) => {
    onUpdate({ ...audit, signatureMoments: moments.filter((_, i) => i !== index) });
  };

  const addMoment = () => {
    onUpdate({ ...audit, signatureMoments: [...moments, { title: '', description: '', why: '', revenueImpact: '', difficulty: 'Medium', locked: false }] });
  };

  return (
    <div className="card p-6">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h3 className="font-display text-lg text-white">Signature Moment Generator</h3>
          <p className="text-xs text-neutral-500">"Only here" moments that create memories and differentiation</p>
        </div>
        <div className="flex items-center gap-2">
          {moments.length > 0 && (
            <span className="text-[9px] text-neutral-600">
              {lockedCount > 0 && <span className="text-brand-gold">{lockedCount} locked</span>}
              {lockedCount > 0 && unlockedCount > 0 && ' · '}
              {unlockedCount > 0 && `${unlockedCount} unlocked`}
            </span>
          )}
          <button onClick={addMoment} className="btn-ghost text-xs">+ Add</button>
          <button onClick={generateMoments} disabled={generating} className="btn-primary text-xs py-2 disabled:opacity-50">
            {generating ? 'Generating...' : lockedCount > 0 && unlockedCount > 0 ? `Regenerate ${unlockedCount}` : moments.length > 0 ? 'Regenerate' : 'Generate Moments'}
          </button>
        </div>
      </div>

      {moments.length > 0 ? (
        <div className="space-y-3">
          {moments.map((m, i) => (
            <div key={i} className={`bg-neutral-800/20 border rounded-lg p-4 transition-all ${m.locked ? 'border-brand-gold/40' : 'border-neutral-800'}`}>
              <div className="flex items-start justify-between mb-2">
                <div className="flex items-center gap-2 flex-1 mr-2">
                  <button onClick={() => toggleLock(i)} className={`text-sm flex-shrink-0 transition-colors ${m.locked ? 'text-brand-gold' : 'text-neutral-700 hover:text-neutral-400'}`} title={m.locked ? 'Unlock — will be replaced on regenerate' : 'Lock — keep on regenerate'}>
                    {m.locked ? '🔒' : '🔓'}
                  </button>
                  <input type="text" value={m.title || ''} onChange={e => updateMoment(i, 'title', e.target.value)} placeholder="Moment title..." className="input-field text-sm font-semibold py-1 flex-1" />
                </div>
                <select value={m.difficulty || 'Medium'} onChange={e => updateMoment(i, 'difficulty', e.target.value)} className={`text-[9px] px-2 py-1 rounded font-medium border-0 ${m.difficulty === 'Easy' ? 'bg-emerald-400/20 text-emerald-400' : m.difficulty === 'Medium' ? 'bg-amber-400/20 text-amber-400' : 'bg-red-400/20 text-red-400'}`}>
                  <option value="Easy">Easy</option>
                  <option value="Medium">Medium</option>
                  <option value="Hard">Hard</option>
                </select>
                <button onClick={() => deleteMoment(i)} className="text-neutral-600 hover:text-red-400 text-xs ml-2">✕</button>
              </div>
              <textarea value={m.description || ''} onChange={e => updateMoment(i, 'description', e.target.value)} placeholder="Description..." rows={2} className="input-field text-xs mb-2" />
              <input type="text" value={m.why || ''} onChange={e => updateMoment(i, 'why', e.target.value)} placeholder="Why this works..." className="input-field text-xs py-1.5 mb-2" />
              <input type="text" value={m.revenueImpact || ''} onChange={e => updateMoment(i, 'revenueImpact', e.target.value)} placeholder="Revenue impact estimate..." className="input-field text-xs py-1.5" />
            </div>
          ))}
        </div>
      ) : (
        <p className="text-neutral-600 text-sm text-center py-6">Click "Generate Moments" or "+ Add" to create signature experiences.</p>
      )}
    </div>
  );
}
